import { SourceStatus, SourceUploadProgress } from '@wove/api/types'
import { memo } from 'react'
import pickBy from 'lodash/pickBy'
import { FileText, CheckCircle2, AlertCircle, X, RefreshCcw } from 'lucide-react'
import { Button, cn, Badge } from '@wove/web-ui'
import { GetSourcesQuery } from '@wove/api/types'
import { format } from 'date-fns'
import { formatFileSize } from '@src/util'

interface UploadStatusOverlayProps {
  sourceStatuses: Record<string, { fileName: string; progress: SourceUploadProgress }>
  completedSources: Record<string, true>
  onRemove: (id: string) => void
  sources?: GetSourcesQuery['getSources']
}
const UploadStatusOverlay = ({
  sourceStatuses: unfilteredSourceStatuses,
  completedSources,
  onRemove,
  sources,
}: UploadStatusOverlayProps) => {
  const sourceStatuses = pickBy(unfilteredSourceStatuses, (_source, key) => !completedSources[key])
  const length = Object.keys(sourceStatuses).length
  const uploadedFiles = Object.values(sourceStatuses)
  if (length === 0) {
    return null
  }

  const getStatusBadge = (status: SourceStatus) => {
    switch (status) {
      case SourceStatus.Pending:
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Uploading
          </Badge>
        )
      case SourceStatus.Processing:
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            Processing
          </Badge>
        )
      case SourceStatus.Complete:
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Complete
          </Badge>
        )
      case SourceStatus.Failed:
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            Error
          </Badge>
        )
      default:
        return null
    }
  }

  return (
    // <div className="absolute bottom-0 right-0 shadow-lg rounded-t-lg w-[320px]">
    //   <div className="flex items-center text-[#121212] text-xs font-semibold p-3 px-5 rounded-t-lg bg-background-light gap-2">
    //     <img src={spinner} className="animate-spin-slow" />
    //     Uploading {length} file{length === 1 ? '' : 's'}...
    //     <div className="flex items-center gap-2 flex-1 justify-end">
    //       <button onClick={() => setExpanded((prev) => !prev)} className="bg-none border-none p-2">
    //         <img
    //           src={chevronDown}
    //           alt={expanded ? 'grey chevron down' : 'grey chevron up'}
    //           className={`transform ${expanded ? 'rotate-180' : 'rotate-0'} transition-transform`}
    //         />
    //       </button>
    //     </div>
    //   </div>
    //   {expanded &&
    //     Object.keys(sourceStatuses).map((sourceId) => {
    //       return (
    //         <UploadItem
    //           item={sourceStatuses[sourceId]}
    //           key={sourceId}
    //           onOpen={() => onOpenSource(sourceId)}
    //         />
    //       )
    //     })}
    // </div>
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Recently Uploaded Files</h2>
      <div className="space-y-3">
        {uploadedFiles.map((file) => {
          const existingSource = sources?.find((source) => source.id === file.progress.id)
          return (
            <div
              key={file.progress.id}
              className={cn(
                'flex items-center gap-4 rounded-lg border p-4 transition-all duration-300',
                // file.isNew && 'animate-in slide-in-from-top-5 fade-in duration-300',
                // file.status === 'removing' && 'animate-out slide-out-to-right fade-out duration-300',
                // file.status === 'complete' && 'bg-green-50/30 border-green-100',
                file.progress.status === SourceStatus.Failed && 'bg-red-50/30 border-red-100',
              )}
            >
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-md transition-colors duration-300',
                  file.progress.status === SourceStatus.Complete
                    ? 'bg-green-100'
                    : file.progress.status === SourceStatus.Failed
                    ? 'bg-red-100'
                    : 'bg-muted',
                )}
              >
                {file.progress.status === SourceStatus.Complete ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : file.progress.status === SourceStatus.Failed ? (
                  <AlertCircle className="h-6 w-6 text-red-600" />
                ) : (
                  <FileText className="h-6 w-6 text-foreground" />
                )}
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium">{file.fileName}</p>
                    {/* {file.status === "error" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-600"
                              onClick={() => showErrorDetails(file)}
                            >
                              View Issue
                            </Button>
                          )} */}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onRemove(file.progress.id)}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove file</span>
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {existingSource?.size && (
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(existingSource.size)}
                      </p>
                    )}
                    <div className="transition-all duration-300">
                      {getStatusBadge(file.progress.status)}
                    </div>
                    {existingSource && (
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(existingSource.createdAt), 'MMM do, yyyy')}
                      </p>
                    )}
                  </div>
                  {file.progress.status === SourceStatus.Failed && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7"
                      // onClick={() => retryProcessing(file.id)}
                    >
                      <RefreshCcw className="mr-1 h-3.5 w-3.5" />
                      Retry
                    </Button>
                  )}
                </div>
                {file.progress.status === SourceStatus.Pending && (
                  <div className="relative pt-1">
                    <p className="font-semibold text-md">{file.progress.progressText}</p>
                    {/* <Progress value={file.progress} className="h-1.5 transition-all duration-300" /> */}
                    <div
                      className="absolute top-0 left-0 h-1.5 w-full bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-75"
                      style={{
                        animation: 'progressGlow 2s ease-in-out infinite',
                        backgroundSize: '200% 100%',
                      }}
                    />
                  </div>
                )}
                {file.progress.status === SourceStatus.Processing && (
                  <div className="relative pt-1">
                    <div className="h-1.5 w-full bg-yellow-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full"
                        style={{
                          width: '100%',
                          animation: 'processingPulse 1.5s ease-in-out infinite',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default memo(UploadStatusOverlay)
